// ─── Auth ─────────────────────────────────────────────────────────────────────

export interface LoginRequest {
  username: string
  password: string
}

export interface LoginResponse {
  id: string
  userName: string
  token: string
  expiresAt: string
  refreshToken: string | null
  refreshTokenExpiresAt: string | null
}

export interface AuthUser {
  id: string
  username: string
  token: string
  expiresAt: string
  refreshToken: string
  refreshTokenExpiresAt: string
}

// ─── Data Sources ─────────────────────────────────────────────────────────────

export type DataSourceParameterType =
  | 'Text'
  | 'Calendar'
  | 'DateRange'
  | 'Dropdown'
  | 'MultiSelect'

export interface DataSourceInputParameter {
  name: string
  /** Human-readable label in UIs (API requires non-empty on save). */
  caption: string
  isRequired: boolean
  parameterType?: DataSourceParameterType
  optionsSql?: string | null
  dependsOn?: string[]
  /** When false, UIs should not prompt for this parameter (e.g. injected UserId). */
  visible?: boolean
}

/** Options lookup uses the data source that authored the dropdown metadata. */
export interface MergedDataSourceInputParameter extends DataSourceInputParameter {
  optionSourceId?: string
}

export interface GetDataSourceParameterOptionsBody {
  parameterName: string
  parameters?: Record<string, string>
}

export interface DataSourceParameterOptionItem {
  id: string
  name: string
}

export interface GetDataSourceParameterOptionsResponse {
  items: DataSourceParameterOptionItem[]
}

export interface DataSource {
  //id: number
  id: string
  key: string
  description: string
  sqlQuery: string
  isCollection: boolean
  inputParameters?: DataSourceInputParameter[]
  createdAt?: string
  updatedAt?: string
  version?: number
}

export interface DataSourcePagedResponse {
  currentPage: number
  totalPages: number
  pageSize: number
  totalCount: number
  hasPrevious: boolean
  hasNext: boolean
  items: DataSource[]
}

export interface DataSourceSearchParams {
  filter?: string
  pageNumber?: number
  pageSize?: number
}

// ─── Templates ────────────────────────────────────────────────────────────────

export interface Template {
  id: string
  name: string
  code: string
  htmlContent: string
  requiredDataSourceKeys: string[]
  tags?: string[]
  excelConfigJson?: string | null
  updatedAt?: string
  isEnabled?: boolean
  /** Present in export/webhook payloads; not stored on the template entity in the API. */
  isActive?: boolean
  version?: number
}

/** JSON bundle produced by export-pack / consumed by import-pack (templates + referenced data sources). */
export interface TemplateExportPack {
  schemaVersion: number
  exportedAt: string
  templates: Template[]
  dataSources: DataSource[]
  missingDataSourceKeys?: string[]
}

export interface TemplateImportPackResult {
  templatesCreated: number
  templatesUpdated: number
  dataSourcesCreated: number
  dataSourcesUpdated: number
}

export interface CloneTemplateDataSourceNaming {
  sourceKey: string
  newKey: string
}

export interface CloneTemplateRequest {
  name: string
  code: string
  dataSources: CloneTemplateDataSourceNaming[]
}

export interface CloneTemplateResult {
  id: string
  name: string
  code: string
}

export interface CloneDataSourceRequest {
  key: string
}

export interface CloneDataSourceResult {
  id: string
  key: string
}

export interface TemplateSummary {
  id: string
  name: string
  code: string
  updatedAt: string
  requiredDataSourceKeys: string[]
  tags?: string[]
  isEnabled?: boolean
  version?: number
}

export interface TemplatePagedResponse {
  currentPage: number
  totalPages: number
  pageSize: number
  totalCount: number
  hasPrevious: boolean
  hasNext: boolean
  items: TemplateSummary[]
}

export interface TemplateSearchParams {
  filter?: string
  includeDeleted?: boolean
  pageNumber?: number
  pageSize?: number
  tags?: string[]
}

export interface TemplateTagsParams {
  /** When omitted, aggregates tags from all templates. */
  templateIds?: string[]
  templateCodes?: string[]
  includeDeleted?: boolean
}

export interface GetTemplateTagsResponse {
  tags: string[]
}

export interface TemplateActivationState {
  id: string
  isActive: boolean
}

// ─── Webhook Subscriptions ────────────────────────────────────────────────────

export interface WebhookSubscription {
  id: string
  tenant: string
  secretKey: string
  url: string
  eventType: string
  startDateUtc?: string | null
  endDateUtc?: string | null
  isEnabled: boolean
}

export interface WebhookSubscriptionPagedResponse {
  currentPage: number
  totalPages: number
  pageSize: number
  totalCount: number
  hasPrevious: boolean
  hasNext: boolean
  items: WebhookSubscription[]
}

export interface WebhookSubscriptionSearchParams {
  filter?: string
  eventType?: string
  isEnabled?: boolean
  activeOnly?: boolean
  pageNumber?: number
  pageSize?: number
}

// ─── Webhook delivery attempts ────────────────────────────────────────────────

export interface WebhookDeliveryAttempt {
  id: string
  webhookSubscriptionId: string
  tenant: string
  url: string
  responseStatusCode: number | null
  success: boolean
  error: string
  responseBody: string
  timestampUtc: string
  payloadPreview: string
}

export interface WebhookDeliveryAttemptPagedResponse {
  currentPage: number
  totalPages: number
  pageSize: number
  totalCount: number
  hasPrevious: boolean
  hasNext: boolean
  items: WebhookDeliveryAttempt[]
}

export interface WebhookDeliveryAttemptSearchParams {
  webhookSubscriptionId?: string
  success?: boolean
  filter?: string
  pageNumber?: number
  pageSize?: number
}

export interface WebhookDeliveryRetryItemResult {
  sourceAttemptId: string
  newAttemptId: string | null
  ok: boolean
  message: string | null
}

export interface WebhookDeliveryRetryBatchResult {
  items: WebhookDeliveryRetryItemResult[]
}

// ─── Schema ───────────────────────────────────────────────────────────────────

export interface TableSchema {
  tableName: string
  comment: string | null
}

export interface ColumnSchema {
  name: string
  dataType: string
  isNullable: boolean
  comment: string | null
}

export interface PreviewResult {
  count: number
  rows: Record<string, unknown>[]
}

// ─── Render / Export ──────────────────────────────────────────────────────────

export interface RenderRequest {
  parameters?: Record<string, string>
}

export interface PdfOptions {
  format?: 'A4' | 'Letter' | 'A3'
  landscape?: boolean
  printBackground?: boolean
  marginTop?: string
  marginBottom?: string
  marginLeft?: string
  marginRight?: string
  footerTemplate?: string
}

export interface PdfExportRequest extends RenderRequest {
  pdfOptions?: PdfOptions
}

export interface ExcelExportRequest extends RenderRequest {
  sheetName?: string
}

export interface DesignerPreviewRequest {
  htmlContent: string
  requiredDataSourceKeys: string[]
  parameters?: Record<string, string>
}

// ─── Excel Config ─────────────────────────────────────────────────────────────

export interface ExcelCellStyle {
  fontName?: string
  fontSize?: number
  fontColor?: string
  backgroundColor?: string
}

export const DEFAULT_EXCEL_CELL_STYLE = (): ExcelCellStyle => ({
  fontName: '',
  fontSize: undefined,
  fontColor: '',
  backgroundColor: '',
})

export const DEFAULT_EXCEL_STYLE_DEFAULTS = {
  defaultFontName: 'Calibri',
  defaultFontSize: 11,
  headerFontColor: '#ffffff',
  headerBackgroundColor: '#1e2333',
  bodyFontColor: '#000000',
  alternatingRows: true,
  alternatingBackgroundColor: '#f4f6fb',
} as const

export interface ExcelColumnConfig {
  field: string
  header: string
  width?: number
  format?: string
  hidden?: boolean
}

export interface ExcelSheetConfig {
  dataSourceKey: string
  sheetName: string
  freezeHeader: boolean
  autoFilter: boolean
  groupByField?: string
  showSubtotals: boolean
  columns: ExcelColumnConfig[]
}

export interface ExcelConfig {
  sheets: ExcelSheetConfig[]
  includeSummarySheet: boolean
  summarySheetName: string
  defaultFontName?: string
  defaultFontSize?: number
  headerStyle?: ExcelCellStyle
  bodyStyle?: ExcelCellStyle
  alternatingRows?: boolean
  alternatingBackgroundColor?: string
}

// ─── Snippets ─────────────────────────────────────────────────────────────────

export type SnippetType = 'Template' | 'DataSource' | 'InputParameter'

export interface Snippet {
  id: string
  name: string
  description: string
  content: string
  type: SnippetType
  createdAt?: string
  updatedAt?: string
  version?: number
}

export interface SnippetPagedResponse {
  currentPage: number
  totalPages: number
  pageSize: number
  totalCount: number
  hasPrevious: boolean
  hasNext: boolean
  items: Snippet[]
}

export interface SnippetSearchParams {
  filter?: string
  type?: SnippetType
  pageNumber?: number
  pageSize?: number
}

export const SNIPPET_TYPE_LABELS: Record<SnippetType, string> = {
  Template: 'Template',
  DataSource: 'Data Source',
  InputParameter: 'Input Parameter',
}